import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { MagneticButton } from '@/components/MagneticButton'
import { Icon } from '@/components/Icon'
import { PageShell } from '@/components/ui/site-chrome'
import { Chip } from '@/components/lc'
import { usePrefersReducedMotion } from '@/lib/useReducedMotion'
import { cn } from '@/lib/utils'
import type { IconName } from '@/data/content'

/* ================================================================== */
/*  About, take two: fewer cards, more moments. A word marquee, an     */
/*  editorial gap sequence, a scroll-lit beliefs reader, a flip panel  */
/*  and a roadmap. Content adapted from the client's landing copy.     */
/* ================================================================== */

/* ---- rolling word band ------------------------------------------------ */
const BAND = ['Restart', 'Reconnect', 'Rediscover', 'Rebuild', 'Rekindle']

function WordBand() {
  const words = [...BAND, ...BAND, ...BAND, ...BAND]
  return (
    <div className="relative overflow-hidden border-y border-lc-line py-5" style={{ ['--marquee-duration' as string]: '30s' }}>
      <div className="motion-safe:animate-marquee-x flex w-max items-center gap-8 motion-reduce:flex-wrap motion-reduce:justify-center">
        {words.map((w, i) => (
          <span key={i} className="flex items-center gap-8 whitespace-nowrap">
            <span className={cn('font-display text-3xl', i % 2 ? 'text-lc-ink' : 'text-lc-mute/60')}>{w}</span>
            <span className="text-lc-accent" aria-hidden="true">✦</span>
          </span>
        ))}
      </div>
    </div>
  )
}

/* ---- the gap: editorial rows, huge ghost numerals --------------------- */
const GAPS = [
  { n: '01', title: 'Fewer conversations', body: 'The quality of social interaction often drops as professional networks change and personal roles evolve.' },
  { n: '02', title: 'Limited spaces', body: 'Most social infrastructure is built around scrolling, not gathering, leaving a void for adults who want real rooms.' },
  { n: '03', title: 'Reduced expertise', body: 'Access to specialised wisdom for life\u2019s transitions, physical, mental and spiritual, is surprisingly scarce.' },
]

function GapRows() {
  return (
    <div className="space-y-2">
      {GAPS.map((g, i) => (
        <motion.div
          key={g.n}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className={cn('flex flex-col gap-2 border-t border-lc-line py-8 md:flex-row md:items-baseline md:gap-12', i % 2 && 'md:flex-row-reverse md:text-right')}
        >
          <span className="font-display text-[clamp(4rem,3rem+6vw,8rem)] leading-none text-lc-mute/25">{g.n}</span>
          <div className="max-w-xl">
            <h3 className="font-display text-3xl">{g.title}</h3>
            <p className="mt-3 text-lg leading-relaxed text-lc-mute">{g.body}</p>
          </div>
        </motion.div>
      ))}
    </div>
  )
}

/* ---- beliefs: one at a time, dealt like cards ------------------------- */
const BELIEFS = [
  'Age is not a deficit; it is a repository of experience.',
  'Community is the antidote to the isolation modern life imposes.',
  'Learning never plateaus; it only becomes deeper.',
  'Digital connection is a bridge; the destination is offline.',
  'Purpose is rediscovered in every new season of life.',
]
const NUMERALS = ['I', 'II', 'III', 'IV', 'V']

function BeliefDeck() {
  const [i, setI] = useState(0)
  const [paused, setPaused] = useState(false)
  const reduced = usePrefersReducedMotion()

  useEffect(() => {
    if (reduced || paused) return
    const t = window.setInterval(() => setI((v) => (v + 1) % BELIEFS.length), 4500)
    return () => window.clearInterval(t)
  }, [reduced, paused])

  return (
    <section className="px-5 py-20">
      <div className="container-lc">
        <div
          className="lc-glass relative overflow-hidden rounded-card p-8 md:p-14"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <span className="blob absolute -left-20 -top-20 h-64 w-64 opacity-30" style={{ background: '#7C3AED' }} aria-hidden="true" />
          <span className="pointer-events-none absolute -right-6 bottom-0 select-none font-display text-[12rem] leading-none text-lc-ink/5" aria-hidden="true">
            {NUMERALS[i]}
          </span>
          <div className="relative">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="label text-lc-mute">What we believe</p>
              <div className="flex gap-1.5" role="tablist" aria-label="Beliefs">
                {NUMERALS.map((n, k) => (
                  <button
                    key={n}
                    role="tab"
                    aria-selected={k === i}
                    aria-label={`Belief ${n}`}
                    onClick={() => setI(k)}
                    className={cn(
                      'label grid h-9 w-9 place-items-center rounded-full !text-[12px] transition-colors',
                      k === i ? 'bg-lime text-night' : 'lc-glass-soft text-lc-mute hover:text-lc-ink',
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-10 min-h-[9rem] md:min-h-[7rem]">
              <AnimatePresence mode="wait">
                <motion.p
                  key={i}
                  initial={reduced ? false : { opacity: 0, y: 24, rotate: 0.6 }}
                  animate={{ opacity: 1, y: 0, rotate: 0 }}
                  exit={reduced ? undefined : { opacity: 0, y: -16 }}
                  transition={{ type: 'spring', stiffness: 240, damping: 24 }}
                  className="max-w-3xl font-display text-[clamp(1.8rem,1.2rem+2.8vw,3.4rem)] leading-[1.05]"
                >
                  {BELIEFS[i]}
                </motion.p>
              </AnimatePresence>
            </div>
            <p className="label mt-8 !text-[11px] text-lc-mute">{NUMERALS[i]} of V · tap a numeral, or just let it turn</p>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ---- is / is-not flip -------------------------------------------------- */
const FLIP = {
  is: {
    label: 'What LifeCraft4U is',
    tone: 'text-lime',
    items: [
      'A curated ecosystem for conscious growth.',
      'A peer-to-peer network of shared wisdom.',
      'A facilitator of real-world meetups.',
      'A home for lifelong learners who believe their best work is still ahead.',
      'A tribe that values wisdom and authentic connection.',
    ],
  },
  not: {
    label: "What it's not",
    tone: 'text-coral',
    items: [
      'A typical social media scrolling app.',
      'A retirement planning service.',
      'A generic networking club.',
      'A passive entertainment platform.',
      'LinkedIn with softer lighting.',
    ],
  },
} as const

function FlipPanel() {
  const [side, setSide] = useState<'is' | 'not'>('is')
  const active = FLIP[side]
  return (
    <div className="lc-glass relative overflow-hidden rounded-card p-8 md:p-12">
      <span className="blob absolute -right-16 -top-16 h-56 w-56 opacity-30" style={{ background: side === 'is' ? '#7C3AED' : '#FF6B5C' }} aria-hidden="true" />
      <div className="relative">
        <div className="lc-glass-soft inline-flex gap-1 rounded-pill p-1.5" role="tablist">
          {(['is', 'not'] as const).map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={side === k}
              onClick={() => setSide(k)}
              className={cn(
                'rounded-pill px-5 py-2 text-[14px] font-semibold transition-colors',
                side === k ? 'bg-lime text-night' : 'text-lc-mute hover:text-lc-ink',
              )}
            >
              {FLIP[k].label}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.ul
            key={side}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 grid gap-x-10 gap-y-4 md:grid-cols-2"
          >
            {active.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[17px] leading-relaxed">
                <span className={cn('label mt-1.5 shrink-0 !text-[12px]', active.tone)} aria-hidden="true">{side === 'is' ? '＋' : '－'}</span>
                <span className={side === 'is' ? 'text-lc-ink' : 'text-lc-mute'}>{it}</span>
              </li>
            ))}
          </motion.ul>
        </AnimatePresence>
      </div>
    </div>
  )
}

/* ---- looking ahead: roadmap ------------------------------------------- */
const ROAD = [
  { icon: 'compass' as IconName, when: 'Today', what: 'Communities, LifeCrafters, real events' },
  { icon: 'mapPin' as IconName, when: 'Next', what: 'Physical hubs in your city' },
  { icon: 'leaf' as IconName, when: 'Then', what: 'Specialised retreat programs' },
  { icon: 'star' as IconName, when: 'Ahead', what: 'A living library of mid-life wisdom' },
]

export default function About() {
  return (
    <PageShell>
      {/* hero */}
      <section className="px-5 pb-14 pt-36 md:pt-44">
        <div className="container-lc">
          <p className="label text-lc-mute">About LifeCraft4U</p>
          <h1 className="mt-4 max-w-4xl font-display text-5xl">
            A conscious <span className="rounded-lg bg-lime px-3 text-night">restart</span>, not a slowdown.
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-lc-mute">
            Every season of adult life brings its own turning point, and most of us are asked
            to meet it alone. We see it differently: every stage is a powerful moment for growth,
            depth and new beginnings. <span className="font-semibold text-lc-ink">LifeCraft4U exists to support that shift, at any age.</span>
          </p>
        </div>
      </section>

      <WordBand />

      {/* the gap */}
      <section className="px-5 py-20">
        <div className="container-lc">
          <p className="label text-lc-mute">Why we started</p>
          <h2 className="mt-4 max-w-2xl font-display text-4xl">The gap no one was addressing.</h2>
          <div className="mt-12">
            <GapRows />
          </div>
        </div>
      </section>

      {/* beliefs, one at a time */}
      <BeliefDeck />

      {/* is / is not */}
      <section className="px-5 py-20">
        <div className="container-lc">
          <FlipPanel />
          <p className="label mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-lc-mute">
            <span className="flex items-center gap-2"><Icon name="shield" size={13} className="text-lc-accent" /> Every member identity-verified</span>
            <span className="flex items-center gap-2"><Icon name="heart" size={13} className="text-lc-accent" /> Actively moderated, clear conduct guidelines</span>
          </p>
        </div>
      </section>

      {/* looking ahead + closing */}
      <section className="px-5 pb-28 pt-4">
        <div className="container-lc">
          <p className="label text-lc-mute">Looking ahead</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {ROAD.map((r, i) => (
              <div key={r.when} className="relative">
                {i > 0 && <span className="absolute -left-3 top-9 hidden h-px w-3 bg-lc-line lg:block" aria-hidden="true" />}
                <div className={cn('h-full rounded-card p-6', i === 0 ? 'lc-glass' : 'lc-glass-soft')}>
                  <div className="flex items-center justify-between">
                    <span className={cn('grid h-10 w-10 place-items-center rounded-tile text-lc-accent', i === 0 ? 'lc-glass-soft' : 'lc-glass')}>
                      <Icon name={r.icon} size={18} />
                    </span>
                    <Chip small tone={i === 0 ? 'lime' : 'outline'}>{r.when}</Chip>
                  </div>
                  <p className="mt-5 text-[15px] font-medium leading-relaxed text-lc-ink">{r.what}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="lc-glass relative mt-16 overflow-hidden rounded-[36px] px-6 py-16 text-center md:py-20">
            <span className="blob absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 opacity-40" style={{ background: '#7C3AED' }} aria-hidden="true" />
            <div className="relative">
              <h2 className="mx-auto max-w-[22ch] font-display text-4xl">
                You don't need to reinvent yourself. You just need to{' '}
                <span className="inline-block -rotate-1 rounded-[0.24em] bg-lime px-[0.18em] pb-[0.05em] text-night">reconnect</span>.
              </h2>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                <MagneticButton href="/communities" data-cursor="join" className="rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-8 xl:py-4 xl:text-[17px] font-bold text-night">
                  Join as a member
                </MagneticButton>
                <MagneticButton href="/lifecrafter-program" className="lc-glass rounded-pill px-6 py-3 text-[15px] xl:px-8 xl:py-4 xl:text-[17px] font-bold text-lc-ink">
                  Apply as a LifeCrafter
                </MagneticButton>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  )
}
